import api from './api';
import { nullableUuid } from '@/utils/uuid';

export type LoteStatus = 'LIVRE' | 'BLOQUEADO' | 'QUARENTENA';

export interface Lote {
  id: string;
  numero_lote: string;
  produto_id: string;
  produto_codigo?: string | null;
  produto_descricao?: string | null;
  produto_nome?: string | null;
  quantidade: number;
  data_fabricacao: string | null;
  data_validade: string | null;
  status: LoteStatus;
  op_id?: string | null;
  op_numero?: string | null;
  localizacao?: string | null;
  fornecedor?: string | null;
}

export interface LoteInput {
  numero_lote: string;
  produto_id: string;
  quantidade: number;
  data_fabricacao?: string | null;
  data_validade?: string | null;
  status?: LoteStatus;
  op_id?: string | null;
  localizacao?: string | null;
  fornecedor?: string | null;
}

const ENDPOINT = '/industrializacao/lote';

const loteService = {
  async listar(filters: { produto?: string; status?: string; op?: string; search?: string } = {}): Promise<Lote[]> {
    const response = await api.get<Lote[]>(ENDPOINT, { params: filters });
    return response.data;
  },
  async buscarPorId(id: string): Promise<Lote> {
    const response = await api.get<Lote>(`${ENDPOINT}/${id}`);
    return response.data;
  },
  async criar(data: LoteInput): Promise<Lote> {
    const response = await api.post<Lote>(ENDPOINT, {
      ...data,
      produto_id: nullableUuid(data.produto_id) || '',
      op_id: nullableUuid(data.op_id),
    });
    return response.data;
  },
  async atualizar(id: string, data: Partial<LoteInput>): Promise<Lote> {
    const payload = {
      ...data,
      ...('produto_id' in data ? { produto_id: nullableUuid(data.produto_id) || '' } : {}),
      ...('op_id' in data ? { op_id: nullableUuid(data.op_id) } : {}),
    };
    const response = await api.put<Lote>(`${ENDPOINT}/${id}`, payload);
    return response.data;
  },
  async excluir(id: string): Promise<void> {
    await api.delete(`${ENDPOINT}/${id}`);
  },
  /** Bloqueia o lote (exige motivo e responsavel) */
  async bloquear(id: string, motivo: string, responsavel: string): Promise<Lote> {
    const response = await api.post<Lote>(`${ENDPOINT}/${id}/bloquear`, { motivo, responsavel });
    return response.data;
  },
  /** Libera o lote (somente se estiver BLOQUEADO) */
  async liberar(id: string): Promise<Lote> {
    const response = await api.post<Lote>(`${ENDPOINT}/${id}/liberar`);
    return response.data;
  },
  /** Transfere o lote para outra localizacao, registrando movimentacao de estoque */
  async transferir(id: string, data: { destino: string; quantidade: number; responsavel?: string }): Promise<Lote> {
    const response = await api.post<Lote>(`${ENDPOINT}/${id}/transferir`, data);
    return response.data;
  },
};

export default loteService;
